import { __decorate } from "tslib";
import __NG_CLI_RESOURCE__0 from "./schedule.component.html?ngResource";
import { Component, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { trigger, transition, useAnimation } from '@angular/animations';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HOURS_PER_DAY, HOURS_PER_WEEK } from 'Utils/time';
import { Schedule } from '../../schedule';
import flyInOut from './fly-in-out';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DropdownModule } from 'primeng/dropdown';
import { DialogModule } from 'primeng/dialog';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { ColorPickerModule } from 'primeng/colorpicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessagesModule } from 'primeng/messages';
import { TreeSelectModule } from 'primeng/treeselect';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ScheduleManagerService } from '../../services/schedule-manager.service';
import { DaypartsComponent } from '../../../../components/dayparts/dayparts.component';
import { ICalendarParser, Timezone, TimezoneComponent, DateTimeValue, DurationValue, createEvent, CalendarComponent, RecurValue, setConfig as setCalendarJsConfig } from '@nextcloud/calendar-js';
import { FullCalendarModule } from '@fullcalendar/angular';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { TimezoneSelectorComponent } from 'src/ng-app/components/timezone/timezone-selector.component';
import { TimezoneService } from 'src/ng-app/shared/services/timezone.service';
setCalendarJsConfig('PRODID', '-//EPIC iO//DI-SS app');
var EventOption;
(function (EventOption) {
    EventOption["ThisEvent"] = "thisEvent";
    EventOption["ThisAndAllFuture"] = "thisAndAllFuture";
    EventOption["AllEventsInSeries"] = "allEventsInSeries";
})(EventOption || (EventOption = {}));
const secondsPerMinute = 60;
const minutesPerHour = 60;
const hoursPerDay = 24;
const millisecondsPerSecond = 1000;
const dayInSeconds = secondsPerMinute * minutesPerHour * hoursPerDay;
const dayInMilliseconds = dayInSeconds * millisecondsPerSecond;
const NEW_EVENT_CLASS = 'new-schedule-event';
const NEW_EVENT_BG_VAR = '--epic-color-new-schedule-event-bg';
let ScheduleComponent = class ScheduleComponent {
    scheduleManager;
    messageService;
    confirmationService;
    timezoneService;
    schedule;
    scheduleChange = new EventEmitter();
    scheduleSaved = new EventEmitter();
    timezone;
    editSchedule;
    newEvent;
    originalEvent;
    #destroy$ = new Subject();
    ics = '';
    parser;
    parserCalendarComponent;
    calendarDateRange;
    calendarOptions = {
        initialView: 'timeGridWeek',
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        headerToolbar: {
            start: 'timeGridWeek,dayGridMonth,dayGridYear',
            center: 'title',
            end: 'today prev,next'
        },
        selectable: true,
        selectMirror: true,
        select: arg => this.handleDateSelect(arg),
        eventClick: arg => this.handleEventClick(arg),
        eventAdd: eventInfo => this.handleEventAdd(eventInfo),
        eventResize: eventInfo => this.handleEventResize(eventInfo),
        eventDrop: eventInfo => this.handleEventDrop(eventInfo),
        editable: true,
        eventSources: [
            {
                id: 'dynamic',
                editable: true,
                events: (fetchInfo, successCallback) => {
                    this.calendarDateRange = {
                        start: fetchInfo.start,
                        end: fetchInfo.end
                    };
                    const calendarEvents = this.loadCalendarEvents();
                    successCallback(calendarEvents);
                },
            }
        ],
        eventClassNames: (renderProps) => {
            const { event: { backgroundColor, borderColor } } = renderProps;
            const classesToAdd = [];
            if (!backgroundColor && !borderColor) {
                this.getAvailableColor();
                classesToAdd.push(NEW_EVENT_CLASS);
            }
            return classesToAdd;
        },
    };
    calendarComponent;
    scheduleDialog = false;
    scheduleDialogIsEditMode = false;
    showDeleteConfirmDialog;
    scheduleForm;
    fb = inject(FormBuilder);
    selectedDaysOfWeek = [];
    minDate;
    endRepeat = [
        { label: 'Never', value: 'NEVER' },
        { label: 'On Date', value: 'ONDATE' },
        { label: 'After', value: 'AFTER' },
    ];
    daysOfWeek = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
    frequency = [
        { label: 'Never', value: 'NEVER' },
        { label: 'Day', value: 'DAILY' },
        { label: 'Week', value: 'WEEKLY' },
    ];
    eventOptionsList = [
        { label: 'This event', value: EventOption.ThisEvent },
        { label: 'This and all following events', value: EventOption.ThisAndAllFuture },
        { label: 'All events in the series', value: EventOption.AllEventsInSeries }
    ];
    eventOptionsDialog = false;
    selectedEventDeleteOption = EventOption.AllEventsInSeries;
    selectedEventUpdateOption = EventOption.AllEventsInSeries;
    pastEventMessage;
    localTimezone;
    legacyScheduleOptions = {
        hideClear: true
    };
    availableColorsMap = new Map();
    constructor(scheduleManager, messageService, confirmationService, timezoneService) {
        this.scheduleManager = scheduleManager;
        this.messageService = messageService;
        this.confirmationService = confirmationService;
        this.timezoneService = timezoneService;
        this.availableColorsMap = this.getCSSVariableColors(); // Fetch colors from CSS variables
    }
    ngOnInit() {
        this.localTimezone = this.timezoneService.getLocalTimezoneOrUTC();
        this.editSchedule = this.scheduleManager.cloneForEdit(this.schedule);
        this.editSchedule.version = Schedule.DEFAULT_MODEL.version;
        this.setSelectedTimezone();
        this.minDate = new Date();
        this.initICalendar();
        this.createScheduleForm();
        this.handleScheduleFormChange();
        this.pastEventMessage = [{ severity: 'warn', detail: 'This event occurs in the past' }];
    }
    ngAfterViewInit() {
        if (this.calendarComponent) {
            this.calendarComponent.getApi().render();
        }
    }
    ngOnDestroy() {
        this.#destroy$.next(null);
        this.#destroy$.complete();
    }
    setSelectedTimezone() {
        if (!this.editSchedule.timezone) {
            this.editSchedule.timezone = this.localTimezone.timezoneId;
        }
        this.timezone = this.timezoneService.getTimezoneById(this.editSchedule.timezone) || this.localTimezone;
    }
    get form() { return this.scheduleForm.controls; }
    get calendar() { return this.calendarComponent.getApi(); }
    /**
     * Fetch CSS variable colors defined in the SASS file that start with `--epic-color-*`.
     * Stores them in a Map where the keys are the CSS variable names and the values are the color hex codes.
     *
     * @returns {Map<string, string>} A map of CSS variable names to color hex codes.
     */
    getCSSVariableColors() {
        const styles = getComputedStyle(document.documentElement);
        const colors = new Map();
        [
            '--epic-color-primary-deep-blue',
            '--epic-color-primary-purple',
            '--epic-color-primary-light-purple',
            '--epic-color-primary-light-blue',
            '--epic-color-secondary-deep-purple',
        ].forEach((cssVar) => {
            const colorValue = styles.getPropertyValue(cssVar).trim();
            if (colorValue) {
                colors.set(cssVar, colorValue);
            }
        });
        return colors;
    }
    /**
     * Get an available color from the Map of available colors.
     * Ensures the color is not already used by existing events.
     *
     * @returns {string} The first available color or a fallback color.
     */
    getAvailableColor() {
        const usedColors = new Set(this.calendar?.getEvents().map(event => event.backgroundColor) || []);
        let chosenColorSet;
        for (const colorSet of this.availableColorsMap.entries()) {
            chosenColorSet = colorSet;
            const [_cssVar, colorHex] = colorSet;
            if (!usedColors.has(colorHex)) {
                break;
            }
        }
        const [chosenCSSVar, chosenColorHex] = chosenColorSet;
        this.updateCSSVariable(NEW_EVENT_BG_VAR, `var(${chosenCSSVar})`);
        return chosenColorHex;
    }
    /**
     * Updates the value of a CSS variable.
     *
     * @param {string} variableName - The name of the CSS variable (e.g., '--epic-color-primary-deep-blue').
     * @param {string} value - The new value to set (e.g., '#123456').
     */
    updateCSSVariable(variableName, value) {
        document.documentElement.style.setProperty(variableName, value);
    }
    loadCalendarEvents() {
        const newEvents = [];
        for (const calendarComp of this.parser.getItemIterator()) {
            for (const vEvent of calendarComp.getEventIterator()) {
                const { title, recurrenceManager } = vEvent;
                if (vEvent.isRecurrenceException()) {
                    const index = newEvents.findIndex(obj => obj.id === vEvent.id);
                    if (index === -1) {
                        const formattedEventMaster = this.formatFcEvent(title, vEvent);
                        newEvents.push(formattedEventMaster);
                    }
                }
                else {
                    const events = this.getEventSetBetweenDates(recurrenceManager);
                    for (const evt of events) {
                        const formattedEvent = this.formatFcEvent(title, evt);
                        newEvents.push(formattedEvent);
                    }
                }
            }
        }
        return newEvents;
    }
    initICalendar() {
        this.parser = new ICalendarParser();
        let icsString = this.editSchedule.data.ics;
        if (!icsString) {
            const timezoneObject = this.getTimezoneObject();
            let timezoneIcs = null;
            if (timezoneObject !== Timezone.utc && timezoneObject !== Timezone.floating) {
                const calendar = CalendarComponent.fromEmpty();
                calendar.addComponent(TimezoneComponent.fromICALJs(timezoneObject.toICALJs()));
                timezoneIcs = calendar.toICS(false);
            }
            icsString = timezoneIcs;
        }
        this.parser.parse(icsString);
        this.parserCalendarComponent = this.parser._calendarComponent;
    }
    createScheduleForm() {
        this.scheduleForm = new FormGroup({
            id: new FormControl(''),
            title: new FormControl('', [Validators.required, Validators.minLength(2)]),
            location: new FormControl('', [Validators.minLength(2)]),
            description: new FormControl('', [Validators.minLength(2)]),
            start: new FormControl('', [Validators.required]),
            end: new FormControl('', [Validators.required]),
            color: new FormControl('#0000ff', [Validators.required]),
            isAllDay: new FormControl(false, []),
            isRecurrent: new FormControl(false, []),
            isRecurrenceException: new FormControl(false, []),
            frequency: new FormControl('', []),
            byday: new FormControl([], []),
            interval: new FormControl(1, [Validators.min(1)]),
            endRepeat: new FormControl('', []),
            count: new FormControl(1, [Validators.min(1)]),
            until: new FormControl(new Date(), []),
            vEvent: new FormControl('', []),
        });
    }
    onChangeEndDate(endDate) {
        const endDateObject = DateTimeValue.fromJSDate(endDate);
        const startDateObject = DateTimeValue.fromJSDate(this.form.start.value);
        const startDateObj = startDateObject.clone();
        const endDateObj = endDateObject.clone();
        if (endDateObj.compare(startDateObj) === -1) {
            // Is end more than one day before start?
            endDateObj.addDuration(DurationValue.fromSeconds(dayInSeconds));
            if (endDateObj.compare(startDateObj) === -1) {
                const substractHours = -2;
                endDateObject.addDuration(DurationValue.fromSeconds(substractHours * minutesPerHour * secondsPerMinute));
                this.scheduleForm.patchValue({ start: endDateObject.jsDate });
            }
            else {
                // add one day to endDate if the endDate is before the startDate
                endDateObject.addDuration(DurationValue.fromSeconds(dayInSeconds));
                endDate = new Date(endDate.getTime() + dayInMilliseconds);
            }
        }
        this.scheduleForm.patchValue({ end: endDate });
    }
    onChangeStartDate(startDate) {
        const endDateObj = DateTimeValue.fromJSDate(this.form.end.value);
        const startDateObj = DateTimeValue.fromJSDate(startDate);
        if (endDateObj.compare(startDateObj) === -1) {
            const newEndDate = this.getDateFromDateTimeValue(startDateObj);
            this.scheduleForm.patchValue({ end: newEndDate });
        }
        this.scheduleForm.patchValue({ start: startDate });
    }
    handleScheduleFormChange() {
        this.form.isRecurrent.valueChanges.subscribe((value) => {
            const frequencyControl = this.scheduleForm.get('frequency');
            if (value) {
                frequencyControl.setValidators([Validators.required]);
            }
            else {
                frequencyControl.clearValidators();
            }
            frequencyControl.updateValueAndValidity();
        });
        this.form.frequency.valueChanges.subscribe((value) => {
            const bydayControl = this.scheduleForm.get('byday');
            if (value?.value === 'WEEKLY') {
                bydayControl.setValidators([Validators.required]);
            }
            else {
                bydayControl.clearValidators();
            }
            bydayControl.updateValueAndValidity();
        });
    }
    patchScheduleForm(eventInfo) {
        const eventData = eventInfo?.event;
        const eventExtendedPropsData = eventData?.extendedProps;
        const dayOfWeekIndex = eventData?.start?.getDay();
        const offsetDayOfWeek = dayOfWeekIndex === 0 ? 6 : dayOfWeekIndex - 1;
        const targetDays = eventExtendedPropsData?.byday ?? [this.daysOfWeek[offsetDayOfWeek]];
        const mappedDays = targetDays.map((day) => this.daysOfWeek.indexOf(day));
        const frequencyValue = eventExtendedPropsData?.frequency ?
            this.frequency.find(item => item.value === eventExtendedPropsData?.frequency) :
            this.frequency[0];
        const endRepeatValue = eventExtendedPropsData?.endRepeat ?? this.endRepeat[0];
        this.scheduleForm.patchValue({
            id: eventData?.id,
            title: eventData?.title,
            start: eventData?.start,
            end: eventData?.end || eventData?.start,
            color: eventData?.backgroundColor,
            description: eventExtendedPropsData?.description,
            location: eventExtendedPropsData?.location,
            isRecurrent: eventExtendedPropsData?.isRecurrent,
            isRecurrenceException: eventExtendedPropsData?.isRecurrenceException,
            frequency: frequencyValue,
            interval: eventExtendedPropsData?.interval,
            byday: mappedDays,
            endRepeat: endRepeatValue,
            count: eventExtendedPropsData?.count,
            until: eventExtendedPropsData?.until?.jsDate || new Date(),
            vEvent: eventExtendedPropsData?.vEvent,
        });
        this.selectedDaysOfWeek = mappedDays;
    }
    saveScheduleDialog() {
        if (!this.scheduleForm.valid) {
            return;
        }
        this.scheduleDialog = false;
        this.handleUpdateParserEvent(this.scheduleForm.value);
    }
    handleDateSelect(arg) {
        if (this.scheduleDialog) {
            const calendarApi = arg.view.calendar;
            calendarApi.unselect();
            return;
        }
        const title = 'New Event';
        const calendarApi = arg.view.calendar;
        calendarApi.unselect();
        this.selectedEventUpdateOption = EventOption.AllEventsInSeries;
        const timezone = this.getTimezoneObject();
        const startDateTime = DateTimeValue.fromJSDate(arg.start);
        startDateTime.replaceTimezone(timezone);
        const endDateTime = DateTimeValue.fromJSDate(arg.end);
        endDateTime.replaceTimezone(timezone);
        const newCalendarComponent = createEvent(startDateTime, endDateTime);
        const color = this.getAvailableColor(); // Get a unique color
        for (const vObject of newCalendarComponent.getVObjectIterator()) {
            vObject.title = title;
            vObject.color = color;
            vObject.undirtify();
            this.parser._addItem(vObject);
            this.parserCalendarComponent.addComponent(vObject);
            const newEvt = this.formatFcEvent(title, vObject);
            calendarApi.addEvent(newEvt, 'dynamic');
        }
    }
    handleEventClick(eventInfo) {
        this.showDialog(eventInfo, true);
    }
    handleEventAdd(eventInfo) {
        this.showDialog(eventInfo);
    }
    handleEventDrop(eventInfo) {
        const { delta, event, revert } = eventInfo;
        const fcAPI = this.calendarComponent.getApi();
        const deltaDuration = this.getDurationValueFromFullCalendarDuration(delta);
        const defaultAllDayDuration = this.getDurationValueFromFullCalendarDuration(fcAPI.getOption('defaultAllDayEventDuration'));
        const defaultTimedDuration = this.getDurationValueFromFullCalendarDuration(fcAPI.getOption('defaultTimedEventDuration'));
        const timezoneId = fcAPI.getOption('timeZone');
        const timezone = timezoneId === 'local' ?
            this.timezoneService.getLocalTimezoneOrUTC() :
            this.timezoneService.getTimezoneOrUTC(timezoneId);
        if (!deltaDuration || !defaultAllDayDuration || !defaultTimedDuration) {
            revert();
            return;
        }
        const eventComponent = event.extendedProps.vEvent;
        try {
            eventComponent.shiftByDuration(deltaDuration, event.allDay, timezone, defaultAllDayDuration, defaultTimedDuration);
        }
        catch (err) {
            console.error('Error shifting event: ', err);
            revert();
            return;
        }
        let original = null;
        let fork = null;
        if (eventComponent.canCreateRecurrenceExceptions()) {
            [original, fork] = eventComponent.createRecurrenceException();
            if (original !== null && fork !== null && original !== fork) {
                this.parserCalendarComponent.addComponent(fork);
            }
        }
        this.updateAndStoreICS();
    }
    handleEventResize(eventInfo) {
        const { event, startDelta, endDelta, revert } = eventInfo;
        const startDeltaDuration = this.getDurationValueFromFullCalendarDuration(startDelta);
        const endDeltaDuration = this.getDurationValueFromFullCalendarDuration(endDelta);
        if (!startDeltaDuration && !endDeltaDuration) {
            revert();
            return;
        }
        const eventComponent = event.extendedProps.vEvent;
        if (startDeltaDuration) {
            eventComponent.addDurationToStart(startDeltaDuration);
        }
        if (endDeltaDuration) {
            eventComponent.addDurationToEnd(endDeltaDuration);
        }
        let original = null;
        let fork = null;
        if (eventComponent.canCreateRecurrenceExceptions()) {
            [original, fork] = eventComponent.createRecurrenceException();
            if (original !== null && fork !== null && original !== fork) {
                this.parserCalendarComponent.addComponent(fork);
            }
        }
        this.updateAndStoreICS();
    }
    handleUpdateParserEvent(eventFormData) {
        this.scheduleDialogIsEditMode = true;
        const formData = eventFormData.id ? eventFormData : eventFormData.event;
        const editAllowedOption = new Set([EventOption.ThisEvent, EventOption.ThisAndAllFuture]);
        for (const calendarComponent of this.parser.getItemIterator()) {
            for (const eventComp of calendarComponent.getVObjectIterator()) {
                // Only for events that belong to a recurrence series
                if (editAllowedOption.has(this.selectedEventUpdateOption)) {
                    this.updateThisEventAndAllFuture(eventComp, formData);
                }
                else {
                    const masterEventId = formData?.vEvent?.masterItem?.id;
                    if (masterEventId === eventComp.id) {
                        this.updateMasterEvent(eventComp, formData);
                    }
                }
            }
        }
        this.initICalendar();
        this.calendar.refetchEvents();
    }
    updateMasterEvent(masterEvent, formData) {
        this.updateEventProperties(masterEvent, formData);
        this.changeRecurrenceFrequency(masterEvent, formData);
        this.updateAndStoreICS();
    }
    updateThisEventAndAllFuture(vEvent, formData) {
        const { id } = formData;
        const { recurrenceManager } = vEvent;
        const thisAndAllFuture = this.selectedEventUpdateOption !== EventOption.ThisEvent;
        const recurEventSeries = this.getEventSetBetweenDates(recurrenceManager);
        for (const recurringEventItem of recurEventSeries) {
            if (id === recurringEventItem.id) {
                const itemIsMasterInSeries = vEvent.id === recurringEventItem.id;
                if (itemIsMasterInSeries) {
                    this.updateEventProperties(vEvent, formData);
                    this.changeRecurrenceFrequency(vEvent, formData);
                }
                else {
                    this.updateEventProperties(recurringEventItem, formData);
                    const isForkedItem = recurringEventItem.primaryItem !== null;
                    let original = null;
                    let fork = null;
                    if (isForkedItem && recurringEventItem.canCreateRecurrenceExceptions()) {
                        [original, fork] = recurringEventItem.createRecurrenceException(thisAndAllFuture);
                        this.changeRecurrenceFrequency(fork, formData);
                    }
                    if (original !== null && fork !== null && original !== fork) {
                        this.parserCalendarComponent.addComponent(fork);
                    }
                }
                this.updateAndStoreICS();
            }
        }
    }
    updateEventProperties(event, formData) {
        const { start, end, title, description, location, color } = formData;
        const timezone = this.getTimezoneObject();
        const startDateTime = DateTimeValue.fromJSDate(start);
        startDateTime.replaceTimezone(timezone);
        const endDateTime = DateTimeValue.fromJSDate(end);
        endDateTime.replaceTimezone(timezone);
        event.updatePropertyWithValue('DTSTART', startDateTime);
        event.updatePropertyWithValue('DTEND', endDateTime);
        event.updatePropertyWithValue('SUMMARY', title);
        event.updatePropertyWithValue('DESCRIPTION', description);
        event.updatePropertyWithValue('LOCATION', location);
        event.updatePropertyWithValue('COLOR', color);
    }
    selectDay(index) {
        const selectedIndex = this.selectedDaysOfWeek.indexOf(index);
        if (selectedIndex === -1) {
            this.selectedDaysOfWeek.push(index);
        }
        else {
            this.selectedDaysOfWeek.splice(selectedIndex, 1);
        }
        this.scheduleForm.patchValue({ byday: this.selectedDaysOfWeek });
    }
    showDialog(eventInfo, isEditMode = false) {
        if (eventInfo) {
            this.patchScheduleForm(eventInfo);
            const fcEvent = this.calendar.getEventById(this.form.id.value);
            this.newEvent = fcEvent;
            if (isEditMode) {
                this.originalEvent = fcEvent;
            }
            this.scheduleDialogIsEditMode = isEditMode;
            const isRecurrenceException = eventInfo?.event?.extendedProps?.isRecurrenceException;
            const isRecurrent = eventInfo?.event?.extendedProps?.isRecurrent;
            if (isEditMode && isRecurrent) {
                this.eventOptionsDialog = true;
            }
            else {
                if (isRecurrenceException) {
                    this.selectedEventUpdateOption = EventOption.ThisEvent;
                    this.selectedEventDeleteOption = EventOption.ThisEvent;
                }
                this.scheduleDialog = true;
            }
        }
    }
    onChangeEditEventOption(editOption) {
        this.selectedEventUpdateOption = editOption.value;
        if (this.selectedEventUpdateOption === EventOption.AllEventsInSeries) {
            const masterItem = this.scheduleForm.value.vEvent.masterItem;
            const startDate = masterItem.startDate.clone();
            const endDate = masterItem.endDate.clone();
            startDate.replaceTimezone(this.localTimezone);
            endDate.replaceTimezone(this.localTimezone);
            this.scheduleForm.patchValue({
                start: startDate.jsDate,
                end: endDate.jsDate
            });
        }
        this.scheduleDialog = true;
        this.eventOptionsDialog = false;
    }
    onChangeDeleteEventOption(deleteOption) {
        this.selectedEventDeleteOption = deleteOption.value;
        this.eventOptionsDialog = false;
        this.deleteEventAndCloseDialog();
    }
    hideDialog() {
        const vEventToRemove = this.newEvent?.extendedProps?.vEvent;
        if (!this.scheduleDialogIsEditMode && vEventToRemove) {
            this.removeEventFromCalendarComponent(vEventToRemove, false);
        }
        this.newEvent = null;
        this.scheduleDialog = false;
    }
    deleteEventAndCloseDialog() {
        const currentEvent = this.scheduleForm.value.vEvent;
        const eventMasterItem = currentEvent?.masterItem;
        const isEventRecurring = eventMasterItem.isRecurring();
        const isExactForkOfPrimary = currentEvent.isExactForkOfPrimary;
        const eventAllowedOption = new Set([EventOption.ThisEvent, EventOption.ThisAndAllFuture]);
        let deleteText = 'this event';
        if (isEventRecurring) {
            if (this.selectedEventDeleteOption === EventOption.ThisEvent) {
                deleteText = 'this occurrence';
            }
            else if (this.selectedEventDeleteOption === EventOption.ThisAndAllFuture) {
                deleteText = 'this occurrence and all following occurrences of this series';
            }
            else if (this.selectedEventDeleteOption === EventOption.AllEventsInSeries) {
                deleteText = 'the recurring series';
            }
        }
        this.confirmationService.confirm({
            message: `Are you sure you want to delete ${deleteText}?`,
            header: 'Confirm',
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'reject',
            acceptLabel: 'Delete',
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-secondary',
            accept: () => {
                // If the master item is being deleted, ensure the entire series and
                // all its recurrence exceptions are deleted.
                if (eventAllowedOption.has(this.selectedEventDeleteOption) && isExactForkOfPrimary) {
                    this.selectedEventDeleteOption = EventOption.AllEventsInSeries;
                }
                for (const calendar of this.parser.getAllItems()) {
                    for (const vEvent of calendar.getEventIterator()) {
                        // Only for events that belong to a recurrence series
                        if (eventAllowedOption.has(this.selectedEventDeleteOption)) {
                            this.deleteThisEventAndAllFuture(vEvent);
                        }
                        else {
                            this.deleteEntireSeries(vEvent);
                        }
                    }
                }
                this.initICalendar();
                this.calendar.refetchEvents();
            }
        });
    }
    deleteEntireSeries(vEvent) {
        let masterItemId = this.scheduleForm.value.vEvent?.id;
        const isForkedItem = this.scheduleForm.value.vEvent?.primaryItem !== null;
        if (isForkedItem) {
            masterItemId = this.scheduleForm.value.vEvent?.masterItem.id;
        }
        if (masterItemId === vEvent.id) {
            this.removeEventFromCalendarComponent(vEvent, true, isForkedItem);
        }
    }
    deleteThisEventAndAllFuture(vEvent) {
        const deleteEventId = this.form.id.value;
        const { recurrenceManager } = vEvent;
        const eventSet = this.getEventSetBetweenDates(recurrenceManager);
        for (const eventSetItem of eventSet) {
            if (deleteEventId === eventSetItem.id) {
                this.removeEventFromCalendarComponent(eventSetItem, true);
            }
        }
    }
    removeEventFromCalendarComponent(event, updateModel, isForkedItem = false) {
        const thisAndAllFuture = this.selectedEventDeleteOption !== EventOption.ThisEvent;
        const { recurrenceManager } = event;
        // Delete the recurrence exception associated with a recurrence series
        if (event.isRecurrenceException() && !event.modifiesFuture()) {
            recurrenceManager.removeRecurrenceException(event);
            this.parserCalendarComponent.deleteComponent(event);
        }
        const deleteOptions = new Set([EventOption.AllEventsInSeries]);
        // Delete all recurrence exceptions associated with a recurrence series
        // when the series itself is being deleted
        if (deleteOptions.has(this.selectedEventDeleteOption) && isForkedItem) {
            const relatedRecurrenceExc = recurrenceManager.getRecurrenceExceptionList();
            if (relatedRecurrenceExc.length) {
                for (const exception of relatedRecurrenceExc) {
                    recurrenceManager.removeRecurrenceException(exception);
                    this.parserCalendarComponent.deleteComponent(exception);
                }
            }
        }
        const isRecurrenceSetEmpty = event.removeThisOccurrence(thisAndAllFuture);
        if (isRecurrenceSetEmpty) {
            this.parserCalendarComponent.deleteComponent(event);
        }
        this.ics = this.parserCalendarComponent.toICS();
        if (updateModel) {
            this.updateSchedule({ ics: this.ics });
        }
        this.selectedEventDeleteOption = EventOption.AllEventsInSeries;
        this.calendar.getEventById(this.form.id.value)?.remove();
        this.scheduleForm.reset();
        this.scheduleDialog = false;
    }
    getDurationValueFromFullCalendarDuration(fcDuration) {
        switch (typeof fcDuration) {
            case 'object':
                return this.getDurationValueFromFullCalendarDurationEncodedAsObject(fcDuration);
            case 'string':
                return this.getDurationValueFromFullCalendarDurationEncodedAsString(fcDuration);
            case 'number':
                return this.getDurationValueFromFullCalendarDurationEncodedAsNumber(fcDuration);
            default:
                return null;
        }
    }
    getDurationValueFromFullCalendarDurationEncodedAsNumber(fcDuration) {
        return DurationValue.fromSeconds(Math.floor(fcDuration / 1000));
    }
    getDurationValueFromFullCalendarDurationEncodedAsString(fcDuration) {
        const match1 = fcDuration.match(/(\d{2}):(\d{2}):(\d{2})\.(\d{3})/);
        if (match1) {
            const [, hours, minutes, seconds] = match1;
            return DurationValue.fromData({
                hours: parseInt(hours, 10),
                minutes: parseInt(minutes, 10),
                seconds: parseInt(seconds, 10),
            });
        }
        const match2 = fcDuration.match(/(\d{2}):(\d{2}):(\d{2})/);
        if (match2) {
            const [, hours, minutes, seconds] = match2;
            return DurationValue.fromData({
                hours: parseInt(hours, 10),
                minutes: parseInt(minutes, 10),
                seconds: parseInt(seconds, 10),
            });
        }
        const match3 = fcDuration.match(/(\d{2}):(\d{2})/);
        if (match3) {
            const [, hours, minutes] = match3;
            return DurationValue.fromData({
                hours: parseInt(hours, 10),
                minutes: parseInt(minutes, 10),
            });
        }
        return null;
    }
    getDurationValueFromFullCalendarDurationEncodedAsObject(fcDuration) {
        if (fcDuration.year || fcDuration.years || fcDuration.month || fcDuration.months) {
            return null;
        }
        const durations = [];
        if (fcDuration.days) {
            durations.push(DurationValue.fromData({
                days: fcDuration.days,
            }));
        }
        if (fcDuration.day) {
            durations.push(DurationValue.fromData({
                days: fcDuration.day,
            }));
        }
        if (fcDuration.minutes) {
            durations.push(DurationValue.fromData({
                minutes: fcDuration.minutes,
            }));
        }
        if (fcDuration.minute) {
            durations.push(DurationValue.fromData({
                minutes: fcDuration.minute,
            }));
        }
        if (fcDuration.seconds) {
            durations.push(DurationValue.fromData({
                seconds: fcDuration.seconds,
            }));
        }
        if (fcDuration.second) {
            durations.push(DurationValue.fromData({
                seconds: fcDuration.second,
            }));
        }
        if (fcDuration.milliseconds) {
            durations.push(DurationValue.fromData({
                seconds: Math.floor(fcDuration.milliseconds / 1000),
            }));
        }
        if (fcDuration.millisecond) {
            durations.push(DurationValue.fromData({
                seconds: Math.floor(fcDuration.millisecond / 1000),
            }));
        }
        if (fcDuration.ms) {
            durations.push(DurationValue.fromData({
                seconds: Math.floor(fcDuration.ms / 1000),
            }));
        }
        const duration = DurationValue.fromSeconds(0);
        for (const dur of durations) {
            duration.addDuration(dur);
        }
        return duration;
    }
    updateSchedule(update) {
        this.editSchedule.data = update;
        this.scheduleChange.emit(this.editSchedule);
    }
    tzChanged(node) {
        this.timezone = node;
        this.editSchedule.timezone = this.timezone.timezoneId;
        this.updateEventSchedule();
        this.calendar.refetchEvents();
        this.scheduleChange.emit(this.editSchedule);
    }
    updateEventSchedule() {
        const newTZ = this.getTimezoneObject();
        for (const calendarComp of this.parser.getItemIterator()) {
            for (const vEvent of calendarComp.getEventIterator()) {
                for (const prop of vEvent.getPropertyIterator()) {
                    this.shiftPropertyToTimezone(prop, newTZ);
                }
            }
        }
        this.ics = this.parserCalendarComponent.toICS();
        this.updateSchedule({ ics: this.ics });
    }
    shiftPropertyToTimezone(prop, newTZ) {
        for (const val of prop.getValueIterator()) {
            if (val instanceof DateTimeValue) {
                try {
                    const newVal = val.getInTimezone(newTZ);
                    if (prop.isMultiValue()) {
                        prop.removeValue(val);
                        prop.addValue(newVal);
                    }
                    else {
                        prop.value = newVal;
                    }
                }
                catch (err) {
                    console.log('Error: ', err);
                }
            }
        }
    }
    saveSchedule() {
        try {
            this.scheduleManager.saveSchedule(this.editSchedule)
                .pipe(takeUntil(this.#destroy$))
                .subscribe((msg) => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Schedule Saved',
                    detail: this.editSchedule.name,
                });
                this.scheduleSaved.emit(msg);
                this.initICalendar();
                this.calendar.refetchEvents();
            }, (err) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error Saving',
                    detail: err.data.error,
                });
            });
        }
        catch (err) {
            console.error('Error saving: ', err);
        }
    }
    resetSchedule() {
        this.editSchedule.reset();
        this.setSelectedTimezone();
        this.initICalendar();
        this.calendar.refetchEvents();
    }
    static getOffsetDayAndHour(day, hour, offset) {
        day = Number(day);
        hour = Number(hour);
        const hours = day * HOURS_PER_DAY;
        const offsetHourInWeek = (hours + hour + offset + HOURS_PER_WEEK) % HOURS_PER_WEEK;
        return {
            offsetHourInWeek,
            offsetDay: Math.floor(offsetHourInWeek / HOURS_PER_DAY),
            offsetHour: offsetHourInWeek % HOURS_PER_DAY
        };
    }
    addRecurrenceRule(recurrenceManager, formData) {
        const { frequency, interval, byday, endRepeat, count, until } = formData;
        const recurrenceRuleObject = {
            freq: frequency.value,
            interval
        };
        if (frequency.value === 'WEEKLY' && byday?.length) {
            const repeatOnValue = byday.map((i) => this.daysOfWeek[i]).join(',');
            recurrenceRuleObject.byday = repeatOnValue;
        }
        let untilDate = null;
        if (endRepeat.value === 'AFTER') {
            recurrenceRuleObject.count = count;
            recurrenceRuleObject.until = untilDate;
        }
        else if (endRepeat.value === 'ONDATE') {
            untilDate = DateTimeValue.fromJSDate(until);
            recurrenceRuleObject.until = untilDate;
            recurrenceRuleObject.count = null;
        }
        const recurrenceValue = RecurValue.fromData(recurrenceRuleObject);
        recurrenceManager.addRecurrenceRule(recurrenceValue);
    }
    changeRecurrenceFrequency(vEvent, formData) {
        const { frequency, isRecurrent } = formData;
        const { recurrenceManager } = vEvent;
        const recurrenceRule = recurrenceManager.getRecurrenceRuleList()[0];
        const editAllowedOption = new Set([EventOption.AllEventsInSeries, EventOption.ThisAndAllFuture]);
        if (editAllowedOption.has(this.selectedEventUpdateOption)) {
            if (!isRecurrent && this.originalEvent?.extendedProps?.isRecurrent) {
                recurrenceManager.clearAllRecurrenceRules();
                return;
            }
            const shouldAddRecurrenceRule = frequency.value !== 'NEVER';
            const hasRecurrenceRule = recurrenceRule?.frequency;
            if (shouldAddRecurrenceRule) {
                if (hasRecurrenceRule) {
                    recurrenceManager.clearAllRecurrenceRules();
                }
                this.addRecurrenceRule(recurrenceManager, formData);
            }
        }
    }
    formatFcEvent(title, vEvent) {
        const firstRecurrence = vEvent.recurrenceManager.getRecurrenceRuleList()[0];
        let repeatOn = null;
        if (firstRecurrence?.frequency === 'WEEKLY') {
            const daysParts = firstRecurrence?._innerValue?.parts?.BYDAY;
            repeatOn = this.daysOfWeek.filter(day => daysParts.includes(day));
        }
        const endRepeatValue = firstRecurrence?.count
            ? this.endRepeat.find(repeat => repeat.value === 'AFTER')
            : firstRecurrence?.until
                ? this.endRepeat.find(repeat => repeat.value === 'ONDATE')
                : this.endRepeat.find(repeat => repeat.value === 'NEVER');
        const timezone = this.getTimezoneObject();
        const start = vEvent.startDate.getInTimezone(timezone);
        start.replaceTimezone(this.localTimezone);
        const end = vEvent.endDate.getInTimezone(timezone);
        end.replaceTimezone(this.localTimezone);
        const eventColor = vEvent.color || '#0000ff';
        const eventTextColor = this.generateTextColorForHex(eventColor);
        const newEventApi = {
            title,
            id: vEvent.id,
            allDay: vEvent.isAllDay(),
            start: start.jsDate,
            end: end.jsDate,
            color: eventColor,
            backgroundColor: eventColor,
            borderColor: eventColor,
            textColor: eventTextColor,
            extendedProps: {
                vEvent,
                recurrenceId: vEvent.getReferenceRecurrenceId()?.unixTime || null,
                vEventComponent: vEvent,
                description: vEvent.description || '',
                location: vEvent.location || '',
                isRecurrenceException: vEvent.isRecurrenceException(),
                isRecurrent: vEvent.isRecurring(),
                frequency: firstRecurrence?.frequency || null,
                interval: firstRecurrence?.interval || 1,
                byday: repeatOn || null,
                endRepeat: endRepeatValue,
                count: firstRecurrence?.count || 1,
                until: firstRecurrence?.until || null
            }
        };
        return newEventApi;
    }
    getTimezoneObject(tzid = this.timezone.timezoneId) {
        return this.timezoneService.getTimezoneOrUTC(tzid);
    }
    getDialogHeight() {
        return this.form.isRecurrent.value ? '840px' : '570px';
    }
    getDateFromDateTimeValue(dateTimeValue) {
        return new Date(dateTimeValue.year, dateTimeValue.month - 1, dateTimeValue.day, dateTimeValue.hour, dateTimeValue.minute, 0, 0);
    }
    sameDay(now, sourceDate) {
        return now.getDate() === sourceDate.getDate() &&
            now.getMonth() === sourceDate.getMonth() &&
            now.getFullYear() === sourceDate.getFullYear();
    }
    eventInThePast(startDate) {
        const now = new Date();
        const eventDate = startDate.value;
        if (this.sameDay(now, eventDate)) {
            return false;
        }
        return eventDate.getTime() < now.getTime();
    }
    getEventSetBetweenDates(recurrenceManager) {
        const timezone = this.getTimezoneObject();
        const eventSet = recurrenceManager.getAllOccurrencesBetween(DateTimeValue.fromJSDate(this.calendarDateRange.start).getInTimezone(timezone), DateTimeValue.fromJSDate(this.calendarDateRange.end).getInTimezone(timezone));
        return eventSet;
    }
    updateAndStoreICS() {
        this.ics = this.parserCalendarComponent.toICS();
        this.updateSchedule({ ics: this.ics });
    }
    generateTextColorForHex(hexColor) {
        const rgbColor = this.hexToRGB(hexColor);
        return this.generateTextColorForRGB(rgbColor);
    }
    // Get a text color that's readable on a given background color in RGB format
    generateTextColorForRGB({ red, green, blue }) {
        return this.isLight({ red, green, blue }) ? '#000000' : '#FAFAFA';
    }
    isLight({ red, green, blue }) {
        const RED_WEIGHT = 299;
        const GREEN_WEIGHT = 587;
        const BLUE_WEIGHT = 114;
        const BRIGHTNESS_THRESHOLD = 130;
        const MAX_BRIGHTNESS = 1000;
        const brightness = ((red * RED_WEIGHT) + (green * GREEN_WEIGHT) + (blue * BLUE_WEIGHT)) / MAX_BRIGHTNESS;
        return brightness > BRIGHTNESS_THRESHOLD;
    }
    hexToRGB(hexColor) {
        if (!hexColor) {
            return { red: 0, green: 0, blue: 0 };
        }
        const hex = hexColor.replace(/^#/, '');
        const redHex = hex.substring(0, 2);
        const greenHex = hex.substring(2, 4);
        const blueHex = hex.substring(4, 6);
        const red = parseInt(redHex, 16);
        const green = parseInt(greenHex, 16);
        const blue = parseInt(blueHex, 16);
        return { red, green, blue };
    }
    static ctorParameters = () => [
        { type: ScheduleManagerService },
        { type: MessageService },
        { type: ConfirmationService },
        { type: TimezoneService }
    ];
    static propDecorators = {
        schedule: [{ type: Input }],
        scheduleChange: [{ type: Output }],
        scheduleSaved: [{ type: Output }],
        calendarComponent: [{ type: ViewChild, args: ['calendar',] }]
    };
};
ScheduleComponent = __decorate([
    Component({
        selector: 'schedule',
        template: __NG_CLI_RESOURCE__0,
        providers: [ConfirmationService, MessageService],
        animations: [
            trigger('inOutL', [
                transition(':enter', [
                    useAnimation(flyInOut, { params: { from: '-100%', to: 0 } })
                ]),
                transition(':leave', [
                    useAnimation(flyInOut, { params: { from: 0, to: '-100%' } })
                ]),
            ]),
            trigger('inOutR', [
                transition(':enter', [
                    useAnimation(flyInOut, { params: { from: '100%', to: 0 } })
                ]),
                transition(':leave', [
                    useAnimation(flyInOut, { params: { from: 0, to: '100%' } })
                ])
            ])
        ],
        standalone: true,
        imports: [
            CommonModule,
            AccordionModule,
            ButtonModule,
            ConfirmPopupModule,
            ConfirmDialogModule,
            DropdownModule,
            DialogModule,
            FormsModule,
            ReactiveFormsModule,
            InputSwitchModule,
            InputTextModule,
            ToastModule,
            DaypartsComponent,
            FullCalendarModule,
            CalendarModule,
            CheckboxModule,
            ColorPickerModule,
            InputNumberModule,
            MessagesModule,
            TreeSelectModule,
            OverlayPanelModule,
            TimezoneSelectorComponent,
        ],
    })
], ScheduleComponent);
export { ScheduleComponent };
